import React, { useMemo } from 'react';
import { Frame } from '../templates/Frame/Frame';
import { Frameless } from '../templates/Frameless/Frameless';
import { AppContext, Provider as AppContextProvider } from '../../contexts/AppContext/AppContext';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { useGenericPage } from './useGenericPage';
import { GlobalMenuStore } from '../../contexts/GlobalMenuStore';
import { Composition } from '../../types/api/Composition';
import { isHidden } from '../../lib/BlockUtils/BlockUtils';
import { isPageManagementBlock } from '../../_blocks/PageManagement';
import { PageLockedModal } from '../../_lib/components/PageLockedModal/PageLockedModal';

type GenericPageProperties = {
  composition: Composition;
};

const getHasPageManagement = (composition: Composition) => {
  const breadcrumbSection = composition?.sections?.breadcrumb;
  if (!breadcrumbSection) {
    return false;
  }
  const pageManagement = composition?.sections?.breadcrumb?.blocks?.find(isPageManagementBlock);
  if (!pageManagement || isHidden(pageManagement)) {
    return false;
  }

  if (pageManagement.embedded) {
    const editModeAvailable = composition.editModeInfo.available;
    const { views } = pageManagement.embedded;
    // the defaultView.editModeToggle url is always provided by the block, but may only be considered if the global
    // composition.editModeInfo.available flag is true (i.e. there is at least one block on the page which is editable).
    const hasSonEditModeLink = !!(editModeAvailable && views?.default?.embedded?.editModeToggle);
    const hasNonDefaultViews = !!Object.keys(views).filter(v => v !== 'default').length;
    const hasOn2EditLink = !!views?.default.embedded?.edit;
    const hasPageState = !!views?.default.embedded?.pageState;
    return hasNonDefaultViews || hasSonEditModeLink || hasOn2EditLink || hasPageState;
  }

  return false;
};

export function GenericPage({ composition }: GenericPageProperties) {
  const { windowInnerWidth, accessKeysEnabled, disableAccessKeys, resetAccessKeys, enableAccessKeys } =
    useGenericPage(composition);
  const GenericP = useMemo<AppContext>(
    () =>
      ({
        userInfo: {
          ...composition.userInfo,
          settings: {
            accessKeysEnabled,
            colorScheme: composition.userInfo?.settings?.colorScheme,
          },
        },
        disableAccessKeys,
        enableAccessKeys,
        resetAccessKeys,
        editModeInfo: composition.editModeInfo,
        appVersion: composition.appVersion,
        portalConfiguration: composition.portalConfiguration,
        headMetaInfo: composition.headMetaInfo,
        pageInfo: composition.pageInfo,
        availableSections: {
          pageManagement: getHasPageManagement(composition),
          navigation: !!composition?.sections?.navigation,
        },
        windowInnerWidth,
        publicSessionKey: composition.publicSessionKey,
      }) as AppContext,
    [accessKeysEnabled, windowInnerWidth, composition, disableAccessKeys, enableAccessKeys, resetAccessKeys],
  );

  return (
    <AppContextProvider value={GenericP}>
      <GlobalMenuStore>
        <div id={GlobalDocumentIds.genericPageTemplate} className="content-behind-modal">
          {composition.template === 'Frameless'
            ? (
                <Frameless composition={composition} />
              )
            : (
                <Frame composition={composition} />
              )}
        </div>
      </GlobalMenuStore>
      {composition.editModeInfo.active && composition.pageInfo?.pageLocked === true ? <PageLockedModal /> : null}
    </AppContextProvider>
  );
}
